import { PageActions } from '@/components/ui/page-actions';
import { PageSidebar, type PageSidebarSection } from '@/components/ui/page-sidebar';
import { Card, CardContent } from '@socialflows/shared-lib';
import { useEffect, useMemo, useState } from 'react';
import { Boxes, Eye, Layers, Package, Save } from 'lucide-react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Product } from '@socialflows/shared-lib';
import { ProductEditSkeleton } from './components/product-edit-skeleton';
import { ProductContext, DEFAULT_PRODUCT } from './product-context';
import { useProduct, useCreateProduct, useUpdateProduct } from '@socialflows/shared-lib';
import { useToast } from '@/hooks/use-toast';
import { useSetPageTitle } from '@/hooks/use-page-title';
import { useUnsavedChanges } from '@/hooks/use-unsaved-changes';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';

export default function ProductFormLayout() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: paramsId } = useParams<{ id: string }>();
  useSetPageTitle(paramsId === 'new' ? 'Create a new product' : 'Edit Product', Boxes);

  const [product, setProduct] = useState<Product>(DEFAULT_PRODUCT);
  const [originalProduct, setOriginalProduct] = useState<Product>(DEFAULT_PRODUCT);

  const productId = paramsId === 'new' ? '' : paramsId || '';
  const { data: productData, isLoading: productLoading } = useProduct(productId);
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();

  const loading = paramsId !== 'new' && productLoading;

  const isDirty = useMemo(
    () => JSON.stringify(product) !== JSON.stringify(originalProduct),
    [product, originalProduct]
  );

  const { attemptNavigation, attemptBack, showDialog, onConfirmLeave, onCancelDialog } =
    useUnsavedChanges(isDirty);

  const productsSidebar: PageSidebarSection[] = [
    {
      items: [
        {
          label: 'Products',
          url: '/e-commerce/products',
          icon: Boxes,
          onClick: () => attemptNavigation('/e-commerce/products'),
        },
        {
          label: 'Product Groups',
          url: '/e-commerce/product-groups',
          icon: Layers,
          onClick: () => attemptNavigation('/e-commerce/product-groups'),
        },
        {
          label: 'Product Templates',
          url: '/e-commerce/product-templates',
          icon: Package,
          onClick: () => attemptNavigation('/e-commerce/product-templates'),
        },
      ],
    },
  ];

  useEffect(() => {
    if (paramsId === 'new') {
      setProduct(DEFAULT_PRODUCT);
      setOriginalProduct(DEFAULT_PRODUCT);
      return;
    }

    if (productData) {
      setProduct(productData);
      setOriginalProduct(productData);
    }
  }, [paramsId, productData]);

  const handleSave = async () => {
    // Validate that product has at least 1 image
    if (!product.media || product.media.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Products must have at least 1 image. Please add images in the Media tab.',
        variant: 'destructive',
      });
      return;
    }

    const productDto = {
      accountId: product.accountId,
      label: product.label,
      description: product.description,
      slug: product.slug,
      categoryId: product.categoryId,
      brandId: product.brandId,
      tags: product.tags,
      media: product.media,
      variants: product.variants,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      costPerItem: product.costPerItem,
      status: product.status,
      isVisible: product.isVisible,
      metaTitle: product.metaTitle,
      metaDescription: product.metaDescription,
      customFields: product.customFields,
    };

    try {
      if (paramsId === 'new') {
        await createProductMutation.mutateAsync(productDto);
        toast({
          title: 'Product created',
          description: 'Your product has been created successfully.',
        });
        navigate('/e-commerce/products');
      } else {
        const updated = await updateProductMutation.mutateAsync({
          id: paramsId || '',
          updates: productDto,
        });
        setProduct(updated);
        setOriginalProduct(updated);
        toast({
          title: 'Product updated',
          description: 'Your product has been updated successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to save product: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex h-full w-full">
      <PageSidebar sections={productsSidebar} />
      <div className="flex flex-col flex-1 min-w-0">
        <div className="hidden space-y-6 md:block w-full pb-24">

          <div className="px-4 pt-6 !mt-0 bg-gray-50">
            <Card>
              <CardContent className="space-y-4">
                {loading ? (
                  <div className="py-4">
                    <ProductEditSkeleton />
                  </div>
                ) : (
                  <div className="space-y-8 lg:space-x-12 lg:space-y-0">
                    <div className="py-4">
                      <ProductContext.Provider
                        value={{ product, setProduct, originalProduct, isDirty }}
                      >
                        <Outlet />
                      </ProductContext.Provider>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <PageActions
            cancelAction={{
              label: 'Cancel',
              onClick: () => attemptBack(),
            }}
            secondaryActions={[
              {
                label: 'Preview',
                onClick: () => {},
                icon: Eye,
                variant: 'secondary',
              },
            ]}
            primaryAction={{
              label: 'Save',
              onClick: handleSave,
              icon: Save,
            }}
          />
        </div>
      </div>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirmLeave={onConfirmLeave}
        onCancelDialog={onCancelDialog}
      />
    </div>
  );
}
